import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Search, Trash2, Truck, Download } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { downloadAsCsv } from '@/lib/csv-export';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';
import { useLabel } from '@/lib/labels';

/** ローカルタイムゾーンで今日の日付 (YYYY-MM-DD)。UTC を使うと JST 0-9 時に前日日付になる問題を回避 */
const todayLocal = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

type Store = { store_id: string; store_code: string; store_name: string };

type DeliveryService = {
  delivery_service_id: string;
  store_id: string;
  service_code: string;
  service_name: string;
  active_flg: number;
  effective_from: string;
  effective_to: string | null;
};

type FormState = {
  delivery_service_id?: string;
  store_id: string;
  service_code: string;
  service_name: string;
  active_flg: number;
  effective_from: string;
  effective_to: string;
};

/** コードの半角英数字・ハイフン・アンダースコアのみ許容 (バックエンドの halfWidthCodeSchema と同じ規則) */
const CODE_PATTERN = '[A-Za-z0-9_-]+';

const emptyForm = (): FormState => ({
  store_id: '',
  service_code: '',
  service_name: '',
  active_flg: 1,
  effective_from: todayLocal(),
  effective_to: '',
});

export function DeliveryServicesPage() {
  const t = useLabel();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.roles?.some((r) => r === 'company_admin' || r === 'platform_admin') ?? false;
  // v0.3 (S-11 DL権限管理): export:view権限が無いユーザーにはCSV出力ボタンを非表示にする
  const { hasPermissionForFeatureAction } = useEffectivePermissions();
  const canExport = hasPermissionForFeatureAction('export', 'view');

  const { data: services = [], isLoading } = useQuery<DeliveryService[]>({
    queryKey: ['delivery-services'],
    queryFn: () => apiFetch<DeliveryService[]>('/delivery-services'),
  });
  const { data: stores = [], isLoading: storesLoading } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/stores'),
  });

  const [editing, setEditing] = useState<FormState | null>(null);
  const [deleting, setDeleting] = useState<DeliveryService | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterStore, setFilterStore] = useState<string>('all');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');

  const storeName = (id: string) => stores.find((s) => s.store_id === id)?.store_name ?? id;

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return services.filter((s) => {
      if (filterStore !== 'all' && s.store_id !== filterStore) return false;
      if (filterActive === 'active' && s.active_flg !== 1) return false;
      if (filterActive === 'inactive' && s.active_flg !== 0) return false;
      if (q && !s.service_name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [services, searchText, filterStore, filterActive]);

  // グルーピング: service_name ごとにまとめて表示 (会社契約業者ビュー)
  const groupedByService = useMemo(() => {
    const map = new Map<string, DeliveryService[]>();
    for (const s of filtered) {
      if (!map.has(s.service_name)) map.set(s.service_name, []);
      map.get(s.service_name)!.push(s);
    }
    // 各グループ内で店舗名→開始日でソート
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        const sa = storeName(a.store_id);
        const sb = storeName(b.store_id);
        if (sa !== sb) return sa.localeCompare(sb);
        return a.effective_from.localeCompare(b.effective_from);
      });
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, stores]);

  const createMut = useMutation({
    mutationFn: (form: FormState) => apiFetch('/delivery-services', {
      method: 'POST',
      body: JSON.stringify({
        store_id: form.store_id,
        service_code: form.service_code,
        service_name: form.service_name,
        active_flg: form.active_flg,
        effective_from: form.effective_from,
        effective_to: form.effective_to || null,
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['delivery-services'] });
      setEditing(null);
      toast.success('デリバリー業者を追加しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '追加に失敗しました'),
  });

  const updateMut = useMutation({
    mutationFn: (form: FormState) => apiFetch(`/delivery-services/${form.delivery_service_id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        service_name: form.service_name,
        active_flg: form.active_flg,
        effective_from: form.effective_from,
        effective_to: form.effective_to || null,
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['delivery-services'] });
      setEditing(null);
      toast.success('更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/delivery-services/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['delivery-services'] });
      setDeleting(null);
      toast.success('削除しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'),
  });

  const submitForm = () => {
    if (!editing) return;
    if (!editing.store_id || (!editing.delivery_service_id && !editing.service_code) || !editing.service_name || !editing.effective_from) {
      toast.error('店舗・業者コード・業者名・開始日は必須です');
      return;
    }
    if (editing.effective_to && editing.effective_from > editing.effective_to) {
      toast.error('開始日は終了日より前の日付にしてください');
      return;
    }
    if (editing.delivery_service_id) updateMut.mutate(editing);
    else createMut.mutate(editing);
  };

  const hasAnyFilter = Boolean(searchText || filterStore !== 'all' || filterActive !== 'all');

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-h1 flex items-center gap-2">
            <Truck size={24} className="text-brand-red" /> {t('entity.delivery_service_master', 'デリバリー業者マスタ')}
          </h1>
          <p className="text-sm text-neutral-sub mt-1">
            会社で契約しているデリバリー業者と、各店舗の利用履歴を管理します
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canExport && (
          <button
            type="button"
            disabled={isLoading || storesLoading || filtered.length === 0}
            aria-label="現在の絞込結果をマスタ取込CSV形式で出力"
            onClick={() => {
              const storeCodeMap = new Map(stores.map((s) => [s.store_id, s.store_code]));
              // store_code が引けない孤児行 (削除済み店舗の参照など) は export から除外し、
              // 再取込時に「store_code 不明」エラーになるのを防ぐ
              const skipped: DeliveryService[] = [];
              const rows: Array<Record<string, unknown>> = [];
              for (const s of filtered) {
                const code = storeCodeMap.get(s.store_id);
                if (!code) { skipped.push(s); continue; }
                rows.push({
                  store_code: code,
                  service_code: s.service_code,
                  service_name: s.service_name,
                  active_flg: s.active_flg,
                  effective_from: s.effective_from,
                  effective_to: s.effective_to ?? '',
                });
              }
              if (rows.length === 0) { toast.error('出力対象の行がありません'); return; }
              const headers = ['store_code', 'service_code', 'service_name', 'active_flg', 'effective_from', 'effective_to'];
              downloadAsCsv(`delivery_services_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
              if (skipped.length > 0) toast.error(`店舗コードを解決できず ${skipped.length} 件を除外しました`);
            }}
            className="flex items-center gap-1 min-h-[36px] px-3 py-2 rounded border border-neutral-line text-sm hover:bg-neutral-panel disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            title={
              storesLoading ? '店舗一覧を読み込み中…'
                : filtered.length === 0 ? (hasAnyFilter ? '絞込みを解除してください' : '出力対象がありません')
                : 'マスタ取込 CSV 形式で出力 (再取込可)'
            }
          >
            <Download size={14} /> CSV出力
          </button>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={() => setEditing(emptyForm())}
              className="flex items-center gap-1 min-h-[36px] px-3 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover whitespace-nowrap"
            >
              <Plus size={16} /> 新規追加
            </button>
          )}
        </div>
      </div>

      {/* フィルタバー */}
      <div className="border border-neutral-line rounded-lg bg-white p-3 flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label htmlFor="ds-search" className="block text-xs text-neutral-sub mb-1">業者名で検索</label>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-sub" aria-hidden="true" />
            <input
              id="ds-search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Uber Eats / 出前館 など"
              className="w-full pl-8 pr-3 py-1.5 border border-neutral-line rounded text-sm focus-ring"
            />
          </div>
        </div>
        <div>
          <label htmlFor="ds-filter-store" className="block text-xs text-neutral-sub mb-1">店舗</label>
          <select
            id="ds-filter-store"
            value={filterStore}
            onChange={(e) => setFilterStore(e.target.value)}
            className="px-2 py-1.5 border border-neutral-line rounded text-sm focus-ring"
          >
            <option value="all">全店舗</option>
            {stores.map((s) => <option key={s.store_id} value={s.store_id}>{s.store_name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="ds-filter-active" className="block text-xs text-neutral-sub mb-1">有効/無効</label>
          <select
            id="ds-filter-active"
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value as 'all' | 'active' | 'inactive')}
            className="px-2 py-1.5 border border-neutral-line rounded text-sm focus-ring"
          >
            <option value="all">すべて</option>
            <option value="active">有効のみ</option>
            <option value="inactive">無効のみ</option>
          </select>
        </div>
        {hasAnyFilter && (
          <button
            type="button"
            onClick={() => { setSearchText(''); setFilterStore('all'); setFilterActive('all'); }}
            className="min-h-[36px] px-3 py-1.5 text-sm text-neutral-sub hover:text-brand-red"
          >
            クリア
          </button>
        )}
      </div>

      {/* 一覧 (業者ごとにグルーピング) */}
      {isLoading ? (
        <div className="text-neutral-sub">読み込み中…</div>
      ) : groupedByService.length === 0 ? (
        <div className="p-8 text-center text-neutral-sub border border-neutral-line rounded-lg bg-white">
          {hasAnyFilter ? '条件に一致する業者がありません' : '登録されている業者がありません'}
        </div>
      ) : (
        <div className="space-y-3">
          {groupedByService.map(([serviceName, list]) => (
            <section key={serviceName} className="border border-neutral-line rounded-lg bg-white overflow-hidden">
              <div className="px-4 py-2.5 border-b border-neutral-line bg-neutral-panel flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Truck size={16} className="text-brand-orange" />
                  <span className="font-medium">{serviceName}</span>
                  <span className="text-xs text-neutral-sub">
                    ({list.length} 店舗で利用)
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead className="text-neutral-sub bg-neutral-panel/50 text-left">
                    <tr>
                      <th scope="col" className="px-3 py-2 min-w-[8rem]">{t('field.delivery_service_master.store_id', '店舗')}</th>
                      <th scope="col" className="px-3 py-2 w-28">{t('field.delivery_service_master.service_code', 'コード')}</th>
                      <th scope="col" className="px-3 py-2 w-28">{t('field.delivery_service_master.effective_from', '開始日')}</th>
                      <th scope="col" className="px-3 py-2 w-28">{t('field.delivery_service_master.effective_to', '終了日')}</th>
                      <th scope="col" className="px-3 py-2 w-16 text-center">{t('field.delivery_service_master.active_flg', '状態')}</th>
                      {isAdmin && <th scope="col" className="px-3 py-2 w-20 text-right">操作</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((s) => (
                      <tr key={s.delivery_service_id} className="border-t border-neutral-line hover:bg-neutral-panel/40">
                        <td className="px-3 py-2">{storeName(s.store_id)}</td>
                        <td className="px-3 py-2 font-mono text-xs text-neutral-sub">{s.service_code}</td>
                        <td className="px-3 py-2 tabular-nums">{s.effective_from}</td>
                        <td className="px-3 py-2 tabular-nums text-neutral-sub">{s.effective_to ?? '—'}</td>
                        <td className="px-3 py-2 text-center">
                          {s.active_flg === 1 ? (
                            <span className="inline-block px-2 py-0.5 rounded text-xs bg-green-50 text-green-800 border border-green-200">有効</span>
                          ) : (
                            <span className="inline-block px-2 py-0.5 rounded text-xs bg-neutral-panel text-neutral-sub border border-neutral-line">無効</span>
                          )}
                        </td>
                        {isAdmin && (
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => setEditing({
                                delivery_service_id: s.delivery_service_id,
                                store_id: s.store_id,
                                service_code: s.service_code,
                                service_name: s.service_name,
                                active_flg: s.active_flg,
                                effective_from: s.effective_from,
                                effective_to: s.effective_to ?? '',
                              })}
                              className="p-1.5 text-neutral-sub hover:text-brand-red"
                              aria-label="編集"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleting(s)}
                              className="p-1.5 text-neutral-sub hover:text-warning-text"
                              aria-label="削除"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}

      {/* 追加・編集モーダル (共通 Modal 経由で Escape / overlay-close / focus-trap を担保) */}
      <Modal
        open={editing !== null}
        title={editing?.delivery_service_id ? 'デリバリー業者を編集' : 'デリバリー業者を追加'}
        size="sm"
        onClose={() => setEditing(null)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="px-4 py-2 rounded border border-neutral-line text-sm hover:bg-neutral-panel"
            >
              キャンセル
            </button>
            <button
              type="button"
              onClick={submitForm}
              disabled={createMut.isPending || updateMut.isPending}
              className="flex items-center gap-1 px-4 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover disabled:opacity-50"
            >
              {editing?.delivery_service_id ? '保存' : '追加'}
            </button>
          </>
        }
      >
        {editing && (
          <div className="space-y-3">
            <div>
              <label htmlFor="ds-store" className="block text-sm text-neutral-sub mb-1">店舗 *</label>
              <select
                id="ds-store"
                value={editing.store_id}
                onChange={(e) => setEditing({ ...editing, store_id: e.target.value })}
                disabled={!!editing.delivery_service_id}
                className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring disabled:bg-neutral-panel"
              >
                <option value="">選択してください</option>
                {stores.map((s) => <option key={s.store_id} value={s.store_id}>{s.store_name}</option>)}
              </select>
              {editing.delivery_service_id && (
                <p className="text-xs text-neutral-sub mt-1">店舗は編集できません (新規追加時のみ選択可)</p>
              )}
            </div>
            <div>
              <label htmlFor="ds-service-code" className="block text-sm text-neutral-sub mb-1">業者コード *</label>
              <input
                id="ds-service-code"
                value={editing.service_code}
                onChange={(e) => setEditing({ ...editing, service_code: e.target.value })}
                disabled={!!editing.delivery_service_id}
                pattern={CODE_PATTERN}
                placeholder="例: SVC-120-01"
                maxLength={30}
                className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring disabled:bg-neutral-panel"
              />
              {editing.delivery_service_id ? (
                <p className="text-xs text-neutral-sub mt-1">コードは編集できません</p>
              ) : (
                <p className="text-xs text-neutral-sub mt-1">半角英数字・ハイフン・アンダースコアのみ</p>
              )}
            </div>
            <div>
              <label htmlFor="ds-service-name" className="block text-sm text-neutral-sub mb-1">{t('field.delivery_service_master.service_name', '業者名')} *</label>
              <input
                id="ds-service-name"
                value={editing.service_name}
                onChange={(e) => setEditing({ ...editing, service_name: e.target.value })}
                placeholder="例: Uber Eats / 出前館 / Wolt"
                maxLength={100}
                className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="ds-effective-from" className="block text-sm text-neutral-sub mb-1">開始日 *</label>
                <input
                  id="ds-effective-from"
                  type="date"
                  value={editing.effective_from}
                  onChange={(e) => setEditing({ ...editing, effective_from: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring"
                />
              </div>
              <div>
                <label htmlFor="ds-effective-to" className="block text-sm text-neutral-sub mb-1">終了日</label>
                <input
                  id="ds-effective-to"
                  type="date"
                  value={editing.effective_to}
                  onChange={(e) => setEditing({ ...editing, effective_to: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring"
                />
                <p className="text-xs text-neutral-sub mt-1">空欄=継続中</p>
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.active_flg === 1}
                  onChange={(e) => setEditing({ ...editing, active_flg: e.target.checked ? 1 : 0 })}
                  className="accent-brand-red"
                />
                有効 (現在利用中)
              </label>
            </div>
          </div>
        )}
      </Modal>

      {/* 削除確認 (破壊的操作、ConfirmDialog で overlay-close 無効) */}
      <ConfirmDialog
        open={deleting !== null}
        title="デリバリー業者の削除"
        destructive
        confirmLabel="削除する"
        loading={deleteMut.isPending}
        onClose={() => { if (!deleteMut.isPending) setDeleting(null); }}
        onConfirm={() => { if (deleting) deleteMut.mutate(deleting.delivery_service_id); }}
        message={deleting && (
          <div className="space-y-2">
            <p>
              以下のデリバリー業者を削除します。この操作は<strong className="text-danger-text">取り消せません</strong>。
            </p>
            <div className="p-3 rounded bg-neutral-panel border border-neutral-line text-sm space-y-1">
              <div><span className="text-neutral-sub">店舗：</span>{storeName(deleting.store_id)}</div>
              <div><span className="text-neutral-sub">コード：</span><span className="font-mono">{deleting.service_code}</span></div>
              <div><span className="text-neutral-sub">業者名：</span>{deleting.service_name}</div>
              <div><span className="text-neutral-sub">期間：</span>{deleting.effective_from} 〜 {deleting.effective_to ?? '継続中'}</div>
            </div>
            <p className="text-xs text-neutral-sub">
              既にデリバリー売上データで参照されている場合は削除できません。その場合は無効化 (状態を「無効」に) または終了日設定で対応してください。
            </p>
          </div>
        )}
      />
    </div>
  );
}
